/**
 * Pure cycles helpers — no Vue/Vuex dependencies.
 * Used by Cycles landing page (NXTGEN-5678).
 */

import {
  CYCLE_CREATION_STATUS,
  CYCLE_STAGES,
  FC_ROUTES,
  STAGE_LABELS,
  UPSI_REVIEW_BLOCK_REASON,
  UPSI_STAGE_LABELS,
  UPSI_STAGES,
} from '../../../utils/constants.js';
import {
  CYCLE_FORM_FIELD_LABELS,
  DEFAULT_CYCLES_FILTERS,
  MONTH_FULL_NAMES,
  MONTH_SHORT_NAMES,
} from './cyclesConstants.js';
import { createInitialCyclesFilter } from '../../../utils/cyclesFilterConstants.js';
import appConstants from '../../../../../plugins/constants.js';
import {
  enumerateMonthTokensBetween,
  formatPeriodRange,
  isCustomPeriodFilter,
  monthTokenOverlapsPeriod,
  toDate,
  yearOverlapsPeriod,
} from '../../../utils/periodHelpers.js';

export { DEFAULT_CYCLES_FILTERS, createInitialCyclesFilter };
export { formatPeriodRange, toDate };

/**
 * Frequency tags stored on the cycle as tags. Create still sends them as checklistfromvalue.
 * @param {object|null|undefined} cycle
 * @returns {string[]}
 */
export function frequencyLabelsFromCycle(cycle) {
  if (!cycle || typeof cycle !== 'object') return [];
  if (Array.isArray(cycle.tags)) return [...cycle.tags];
  if (Array.isArray(cycle.checklistfromvalue)) return [...cycle.checklistfromvalue];
  return [];
}

function fieldErrorMessage(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(', ');
  }
  if (value == null || typeof value === 'object') return '';
  return String(value).trim();
}

function isFieldMessageItem(item) {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return false;
  return typeof item.field === 'string' && Object.prototype.hasOwnProperty.call(item, 'message');
}

function flattenObjectFieldErrors(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const indexKey = appConstants.API.VALIDATION_INDEX_KEY;
  const nested = raw[indexKey];
  const source = nested && typeof nested === 'object' && !Array.isArray(nested) ? nested : raw;
  const errors = {};
  Object.keys(source).forEach((field) => {
    if (field === indexKey) return;
    const message = fieldErrorMessage(source[field]);
    if (!message) return;
    const label = CYCLE_FORM_FIELD_LABELS[field] || field;
    errors[field] = `${label} ${message}`;
  });
  return errors;
}

function flattenFieldMessageList(items) {
  const errors = {};
  items.forEach((item) => {
    if (!isFieldMessageItem(item)) return;
    const message = fieldErrorMessage(item.message);
    if (!message) return;
    const field = item.field.startsWith('data.') ? item.field.slice('data.'.length) : item.field;
    const label = CYCLE_FORM_FIELD_LABELS[field] || field;
    errors[field] = `${label} ${message}`;
  });
  return errors;
}

/**
 * Flattens add/edit cycle validation payloads into `{ field: "Form Label message" }`.
 * Accepts the legacy object (`index0` or a flat field map), novadbmongo `errdata`
 * (`[{ index0: { field: [message] } }]`), and V2 Joi `data` (`[{ field, message }]`).
 * A Joi path of `data.description` is stored under `description` so the form can show it.
 *
 * @param {Object|Array|null|undefined} raw
 * @returns {Object<string, string>}
 */
export function flattenCycleFieldErrors(raw) {
  if (!raw) return {};
  if (Array.isArray(raw)) {
    if (raw.some(isFieldMessageItem)) return flattenFieldMessageList(raw);
    return raw.reduce((acc, item) => Object.assign(acc, flattenObjectFieldErrors(item)), {});
  }
  if (isFieldMessageItem(raw)) return flattenFieldMessageList([raw]);
  return flattenObjectFieldErrors(raw);
}

/**
 * @param {Object<string, string>} fieldErrors
 * @param {string} [fallback]
 * @returns {string}
 */
export function formatCycleFieldErrorToast(fieldErrors, fallback = '') {
  const messages = Object.values(fieldErrors || {}).filter(Boolean);
  if (messages.length) return messages.join('; ');
  return fallback;
}

/**
 * Auto-fill cycle name from period, e.g. "Finance Close - March 2026".
 * Uses the `from` date's month/year.
 *
 * @param {string|Date} from
 * @param {string|Date} [_to] Reserved for callers that pass a date range; name uses `from` only.
 * @returns {string}
 */
export function generateCycleName(from) {
  const start = toDate(from);
  if (!start) return '';
  const month = MONTH_FULL_NAMES[start.getUTCMonth()];
  const year = start.getUTCFullYear();
  return `Finance Close - ${month} ${year}`;
}

/**
 * Derive a display-only frequency chip from the period span.
 * Same calendar month → "Monthly"; otherwise "Custom".
 * Not sent to the API (no frequency field on add/edit payload).
 *
 * @param {string|Date} from
 * @param {string|Date} to
 * @returns {string}
 */
export function deriveFrequencyLabel(from, to) {
  const start = toDate(from);
  const end = toDate(to);
  if (!start || !end) return '';
  if (
    start.getUTCFullYear() === end.getUTCFullYear() &&
    start.getUTCMonth() === end.getUTCMonth()
  ) {
    return 'Monthly';
  }
  return 'Custom';
}

/**
 * Human-readable label for selected period filter tokens.
 * e.g. ["2025-03"] → "March 2025"; ["2026"] → "2026"; multiple → joined / count.
 *
 * @param {string[]} periods
 * @returns {string}
 */
export function formatPeriodFilterLabel(periods) {
  const tokens = (periods || []).filter(Boolean);
  if (!tokens.length) return '';

  const toLabel = (token) => {
    if (/^\d{4}$/.test(token)) return token;
    if (/^\d{4}-\d{2}$/.test(token)) {
      const [y, m] = token.split('-');
      const monthName = MONTH_FULL_NAMES[Number(m) - 1];
      return monthName ? `${monthName} ${y}` : token;
    }
    return token;
  };

  if (tokens.length === 1) return toLabel(tokens[0]);
  if (tokens.length === 2) return `${toLabel(tokens[0])} and ${toLabel(tokens[1])}`;
  return `${tokens.length} periods`;
}

/**
 * @param {{ approved?: number, count?: number }|null|undefined} summary
 * @returns {number}
 */
export function calcCompletion(summary) {
  const count = (summary || {}).count || 0;
  if (count === 0) return 0;
  const approved = (summary || {}).approved || 0;
  return Math.round((approved / count) * 100);
}

/**
 * Progress bar segment widths (percent of total).
 *
 * @param {Object} summary
 * @returns {{ approved: number, pendingapproval: number, pending: number }}
 */
export function getProgressSegments(summary) {
  const count = (summary || {}).count || 0;
  if (count === 0) {
    return { approved: 0, pendingapproval: 0, pending: 0 };
  }
  const approved = ((summary || {}).approved || 0) / count * 100;
  const pendingapproval = ((summary || {}).pendingapproval || 0) / count * 100;
  const pending = ((summary || {}).pending || 0) / count * 100;
  return { approved, pendingapproval, pending };
}

/**
 * @param {Object} cycle
 * @returns {boolean}
 */
export function isCycleOpen(cycle) {
  return Boolean(cycle) && cycle.stage !== CYCLE_STAGES.CLOSED;
}

/**
 * @param {Object} cycle
 * @param {string} userId
 * @returns {boolean}
 */
export function isCycleManager(cycle, userId) {
  if (!cycle || !userId) return false;
  return Array.isArray(cycle.managerlist) && cycle.managerlist.includes(userId);
}

/**
 * @param {string} actorRoleLabel
 * @returns {boolean}
 */
export function isActorController(actorRoleLabel) {
  return String(actorRoleLabel || '').trim() === 'controller';
}

/**
 * Cycle manager who is assigned every location on the cycle.
 *
 * @param {Object} [options]
 * @param {Object} options.cycle
 * @param {string} options.userId
 * @param {string[]} [options.actorLocationIds]
 * @returns {boolean}
 */
export function hasFullCycleAccess({
  cycle,
  userId,
  actorLocationIds = [],
} = {}) {
  if (!cycle || !userId || !isCycleManager(cycle, userId)) return false;

  const cycleLocIds = (cycle.location || []).map((id) => String(id)).filter(Boolean);
  if (!cycleLocIds.length) return false;

  const actorSet = new Set((actorLocationIds || []).map((id) => String(id)));
  return cycleLocIds.every((id) => actorSet.has(id));
}

/**
 * Controllers may export. Otherwise the actor must be a cycle manager who covers
 * every location on the cycle (mirrors supernova exportAccess / cycleTeamVisibility).
 *
 * @param {Object} [options]
 * @param {Object} options.cycle
 * @param {string} options.userId
 * @param {string} [options.actorRoleLabel]
 * @param {string[]} [options.actorLocationIds]
 * @returns {boolean}
 */
export function canExportCycleActivityLogs({
  cycle,
  userId,
  actorRoleLabel = '',
  actorLocationIds = [],
} = {}) {
  if (!cycle || !userId) return false;
  if (isActorController(actorRoleLabel)) return true;
  return hasFullCycleAccess({ cycle, userId, actorLocationIds });
}

/**
 * @param {string} status
 * @returns {boolean}
 */
export function isCycleSettingUp(status) {
  return (
    status === CYCLE_CREATION_STATUS.PENDING ||
    status === CYCLE_CREATION_STATUS.IN_PROGRESS
  );
}

/** Fields a lite poll tick may copy onto an existing full row. */
export const LITE_POLL_MERGE_FIELDS = [
  'stage',
  'stage_text',
  'status',
  'cycle_creation_status',
  'description',
  'from',
  'to',
  'managerlist',
];

/**
 * True while any cycle is still being created. completed, partially_completed,
 * and failed are end states, so a list of only those does not poll.
 * @param {object[]} list
 * @returns {boolean}
 */
export function listNeedsCreationPoll(list) {
  return (list || []).some((item) => isCycleSettingUp(item && item.cycle_creation_status));
}

/**
 * True when a cycle that was pending or in_progress now has an end-state status
 * on the lite list. A missing lite row is not treated as finished.
 * @param {object[]} fullRows
 * @param {object[]} liteRows
 * @returns {boolean}
 */
export function polledCycleReachedEndState(fullRows, liteRows) {
  const liteById = new Map();
  (liteRows || []).forEach((row) => {
    if (row && row._id != null) liteById.set(String(row._id), row);
  });
  return (fullRows || []).some((row) => {
    if (!row || !isCycleSettingUp(row.cycle_creation_status)) return false;
    const next = liteById.get(String(row._id));
    if (!next || !Object.prototype.hasOwnProperty.call(next, 'cycle_creation_status')) return false;
    return !isCycleSettingUp(next.cycle_creation_status);
  });
}

/**
 * Copy lite status fields onto the matching full rows. Other full-row fields stay.
 * Rows that are not in the lite list are returned unchanged.
 * @param {object[]} fullRows
 * @param {object[]} liteRows
 * @returns {object[]}
 */
export function mergeLiteCycleFields(fullRows, liteRows) {
  const liteById = new Map();
  (liteRows || []).forEach((row) => {
    if (row && row._id != null) liteById.set(String(row._id), row);
  });
  return (fullRows || []).map((row) => {
    if (!row || row._id == null) return row;
    const lite = liteById.get(String(row._id));
    if (!lite) return row;
    const next = { ...row };
    LITE_POLL_MERGE_FIELDS.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(lite, key)) {
        next[key] = lite[key];
      }
    });
    return next;
  });
}

/**
 * Badge config for cycle creation status.
 * Backgrounds use token-backed SCSS classes (see CycleTableRow.scss).
 *
 * @param {string} status
 * @returns {{ label: string, badgeClass: string, textClass: string, icon: string }}
 */
export function getCycleStatusConfig(status) {
  const configs = {
    [CYCLE_CREATION_STATUS.PENDING]: {
      label: 'Setting Up',
      badgeClass: 'cycle-table-row__status-badge--pending',
      textClass: 'setting-up',
      icon: 'mdi-timer-sand',
    },
    [CYCLE_CREATION_STATUS.IN_PROGRESS]: {
      label: 'Setting Up',
      badgeClass: 'cycle-table-row__status-badge--in-progress',
      textClass: 'setting-up',
      icon: 'mdi-progress-clock',
    },
    [CYCLE_CREATION_STATUS.FAILED]: {
      label: 'SETUP FAILED',
      badgeClass: 'cycle-table-row__status-badge--failed',
      textClass: 'setup-failed',
      icon: 'mdi-alert',
      iconSrc: '/img/icons/fbc-icons/SetupFailed.svg',
    },
  };
  return (
    configs[status] || {
      label: status,
      badgeClass: '',
      textClass: 'grey--text text--darken-1',
      icon: 'mdi-information-outline',
    }
  );
}

/**
 * Close dialog primary CTA: "Close" when fully approved, else "Close anyway".
 *
 * @param {Object} summary
 * @returns {'Close'|'Close anyway'}
 */
export function getCloseConfirmLabel(summary) {
  const count = (summary || {}).count || 0;
  const pending = (summary || {}).pending || 0;
  const pendingapproval = (summary || {}).pendingapproval || 0;
  const approved = (summary || {}).approved || 0;
  if (count > 0 && pending === 0 && pendingapproval === 0 && approved === count) {
    return 'Close';
  }
  return 'Close anyway';
}

/**
 * Short month+year label for confirm dialog titles, e.g. "Jun 2026".
 *
 * @param {string|Date} from
 * @returns {string}
 */
export function formatCycleMonthYear(from) {
  const start = toDate(from);
  if (!start) return '';
  return `${MONTH_SHORT_NAMES[start.getUTCMonth()]} ${start.getUTCFullYear()}`;
}

/**
 * Human-readable status labels used for search matching.
 *
 * @param {Object} cycle
 * @returns {string[]}
 */
export function getCycleSearchStatusLabels(cycle) {
  const labels = [];
  if (!cycle) return labels;

  if (cycle.stage === CYCLE_STAGES.CLOSED) {
    labels.push('closed');
  } else {
    labels.push('open');
    if (cycle.stage === CYCLE_STAGES.PENDING_REVIEW) {
      labels.push('pending for review');
    }
  }

  if (isCycleSettingUp(cycle.cycle_creation_status)) {
    labels.push('setting up');
  }
  if (cycle.cycle_creation_status === CYCLE_CREATION_STATUS.FAILED) {
    labels.push('setup failed');
  }

  const stageLabel = STAGE_LABELS[cycle.stage];
  if (stageLabel) labels.push(String(stageLabel).toLowerCase());

  const upsiLabel = UPSI_STAGE_LABELS[displayedUpsiStage(cycle)];
  if (upsiLabel) labels.push(String(upsiLabel).toLowerCase());

  return labels;
}

/**
 * @param {Object} cycle
 * @param {string} statusTab
 * @returns {boolean}
 */
export function matchStatusTab(cycle, statusTab) {
  if (!statusTab || statusTab === 'all') return true;
  if (statusTab === 'closed') return cycle.stage === CYCLE_STAGES.CLOSED;
  if (statusTab === 'open') return cycle.stage !== CYCLE_STAGES.CLOSED;
  return true;
}

/**
 * Case-insensitive match on name, period text, location names, status labels.
 *
 * @param {Object} cycle
 * @param {string} query
 * @param {Object<string, {name?: string}>} [locationsMap]
 * @returns {boolean}
 */
export function matchSearch(cycle, query, locationsMap = {}) {
  const term = (query || '').trim().toLowerCase();
  if (!term) return true;

  const haystacks = [];

  if (cycle.description) haystacks.push(String(cycle.description));
  if (cycle.periodText) {
    haystacks.push(String(cycle.periodText));
  } else {
    haystacks.push(formatPeriodRange(cycle.from, cycle.to));
  }

  const locationIds = cycle.location || [];
  locationIds.forEach((id) => {
    const loc = locationsMap[id];
    if (loc && loc.name) haystacks.push(loc.name);
  });

  getCycleSearchStatusLabels(cycle).forEach((label) => haystacks.push(label));

  return haystacks.some((text) => String(text).toLowerCase().includes(term));
}

/**
 * Multi-select filter: empty selection means All.
 *
 * @param {string[]} cycleValues
 * @param {string[]} selected
 * @returns {boolean}
 */
export function matchMultiSelect(cycleValues, selected) {
  if (!selected || selected.length === 0 || selected.includes('all')) return true;
  const values = cycleValues || [];
  return selected.some((id) => values.includes(id));
}

/**
 * User association: managerlist or userlist contains any selected user.
 *
 * @param {Object} cycle
 * @param {string[]} selectedUsers
 * @returns {boolean}
 */
export function matchUserAssociation(cycle, selectedUsers) {
  if (!selectedUsers || selectedUsers.length === 0 || selectedUsers.includes('all')) {
    return true;
  }
  const associated = [...(cycle.managerlist || []), ...(cycle.userlist || [])];
  return selectedUsers.some((id) => associated.includes(id));
}

/**
 * UPSI stage multi-select. An empty selection matches every cycle.
 *
 * @param {Object} cycle
 * @param {Array<number|string>} upsiStatuses
 * @returns {boolean}
 */
/**
 * Open cycles are unpublished only. Closed cycles use the stored UPSI stage.
 *
 * @param {Object} cycle
 * @returns {number}
 */
export function displayedUpsiStage(cycle) {
  if (!cycle || Number(cycle.stage) !== CYCLE_STAGES.CLOSED) {
    return UPSI_STAGES.UNPUBLISHED;
  }
  const raw = cycle.upsi_state ? cycle.upsi_state.stage : UPSI_STAGES.UNPUBLISHED;
  const stage = Number(raw);
  return Number.isNaN(stage) ? UPSI_STAGES.UNPUBLISHED : stage;
}

export function matchUpsiStatus(cycle, upsiStatuses) {
  const selected = upsiStatuses || [];
  if (selected.length === 0 || selected.includes('all')) return true;
  return selected.some((value) => Number(value) === displayedUpsiStage(cycle));
}

/**
 * Stored UPSI stage, defaulting to unpublished when the value is missing.
 *
 * @param {Object} cycle
 * @returns {number}
 */
function storedUpsiStage(cycle) {
  const raw = cycle && cycle.upsi_state ? cycle.upsi_state.stage : UPSI_STAGES.UNPUBLISHED;
  const stage = Number(raw);
  return Number.isNaN(stage) ? UPSI_STAGES.UNPUBLISHED : stage;
}

/**
 * Awaiting approval, and this user requested publish (`review_block_reason === own_request`).
 * They are not the reviewer, so the row has no Publish or Review action.
 *
 * @param {Object} cycle
 * @returns {boolean}
 */
export function isUpsiAwaitingPublisher(cycle) {
  if (storedUpsiStage(cycle) !== UPSI_STAGES.PENDING_APPROVAL) return false;
  const reason = cycle && cycle.upsi_permissions ? cycle.upsi_permissions.review_block_reason : '';
  return reason === UPSI_REVIEW_BLOCK_REASON.OWN_REQUEST;
}

/**
 * True when UpsiRowActions owns the reopen control, so the row hides the full Reopen Cycle button.
 * Awaiting owns it for the reviewer and for everyone else who is not the publisher.
 * The awaiting publisher keeps the labeled Reopen Cycle button.
 * Unpublished and Rejected own it only when Publish is shown.
 *
 * @param {Object} cycle
 * @param {boolean} canPublishRight
 * @returns {boolean}
 */
export function upsiActionsReplaceReopen(cycle, canPublishRight) {
  const stage = storedUpsiStage(cycle);
  if (stage === UPSI_STAGES.PENDING_APPROVAL) return !isUpsiAwaitingPublisher(cycle);
  const requestable = stage === UPSI_STAGES.UNPUBLISHED || stage === UPSI_STAGES.REJECTED;
  const canRequest = !!(cycle && cycle.upsi_permissions && cycle.upsi_permissions.can_request);
  return requestable && canRequest && !!canPublishRight;
}

/**
 * Period token match: year "2026" or month "2026-03".
 * Cycle matches if the selected token overlaps any month/year in the period range.
 *
 * @param {Object} cycle
 * @param {string[]} periods
 * @returns {boolean}
 */
export function matchPeriod(cycle, periods) {
  if (!periods || periods.length === 0 || periods.includes('all')) return true;

  const start = toDate(cycle.from);
  const end = toDate(cycle.to);
  if (!start && !end) return false;

  return periods.some((token) => {
    if (!token || token === 'all') return true;
    if (/^\d{4}$/.test(token)) {
      return yearOverlapsPeriod(Number(token), start, end);
    }
    if (/^\d{4}-\d{2}$/.test(token)) {
      return monthTokenOverlapsPeriod(token, start, end);
    }
    return false;
  });
}

/**
 * Full toolbar filter pipeline.
 *
 * @param {Object[]} cycles
 * @param {Object} cyclesFilters
 * @param {Object<string, {name?: string}>} [locationsMap]
 * @returns {Object[]}
 */
export function applyCycleFilters(cycles, cyclesFilters, locationsMap = {}) {
  const filters = { ...DEFAULT_CYCLES_FILTERS, ...(cyclesFilters || {}) };
  return (cycles || [])
    .filter((c) => matchStatusTab(c, filters.statusTab))
    .filter((c) => matchSearch(c, filters.search, locationsMap))
    .filter((c) => matchMultiSelect(c.location, filters.locations))
    .filter((c) => matchUserAssociation(c, filters.users))
    .filter((c) => matchPeriod(c, filters.periods))
    .filter((c) => matchUpsiStatus(c, filters.upsiStatuses));
}

/**
 * Whether any toolbar filter differs from the initial/reset defaults.
 *
 * @param {Object} cyclesFilters
 * @returns {boolean}
 */
export function hasActiveFilters(cyclesFilters) {
  const defaults = createInitialCyclesFilter();
  const filters = { ...defaults, ...(cyclesFilters || {}) };
  if (filters.statusTab !== defaults.statusTab) return true;
  if ((filters.search || '').trim() !== '') return true;
  if ((filters.locations || []).length > 0) return true;
  if ((filters.users || []).length > 0) return true;
  if (isCustomPeriodFilter(filters.periods)) return true;
  if ((filters.upsiStatuses || []).length > 0) return true;
  return false;
}

/**
 * Map API project → table row.
 *
 * @param {Object} raw
 * @param {number} index
 * @param {Object<string, {name?: string}>} [locationsMap]
 * @returns {Object}
 */
export function formatCycleRow(raw, index, locationsMap = {}) {
  const from = raw.from?.split?.('T')?.[0] || raw.from;
  const to = raw.to?.split?.('T')?.[0] || raw.to;
  const start = toDate(from);

  return {
    __key: index,
    __formatedkey: index,
    _id: raw._id,
    indexno: index + 1,
    __click: FC_ROUTES.CHECKLIST(raw._id),
    type: raw.type,
    category: raw.category,
    description: raw.description,
    files: raw.files,
    nopbc: raw.nopbc,
    notification: raw.notification,
    pbcproject: raw.pbcproject,
    from,
    to,
    periodText: formatPeriodRange(from, to),
    stage: raw.stage,
    stage_text: raw.stage_text == null ? '' : raw.stage_text,
    upsi_state: raw.upsi_state ? { ...raw.upsi_state } : null,
    upsi_permissions: raw.upsi_permissions ? { ...raw.upsi_permissions } : null,
    summary: raw.summary,
    created_at: raw.created_at?.split?.('T')?.[0] || raw.created_at,
    updated_at: raw.updated_at?.split?.('T')?.[0] || raw.updated_at,
    year: start ? start.getUTCFullYear() : null,
    month: start ? MONTH_FULL_NAMES[start.getUTCMonth()] : null,
    status: raw.status,
    managerlist: raw.managerlist || [],
    conversation_read: raw.conversation_read,
    userlist: raw.userlist || [],
    location: raw.location || [],
    locationNames: (raw.location || [])
      .map((id) => (locationsMap[id] && locationsMap[id].name) || id)
      .filter(Boolean),
    ...(Array.isArray(raw.tags) ? { tags: [...raw.tags] } : {}),
    checklistfrom: raw.checklistfrom == null ? '' : raw.checklistfrom,
    cycle_type: raw.cycle_type == null ? '' : raw.cycle_type,
    cycle_creation_status: raw.cycle_creation_status || CYCLE_CREATION_STATUS.COMPLETED,
    cycle_creation_error_message: raw.cycle_creation_error_message || null,
    completionPct: calcCompletion(raw.summary),
  };
}

/**
 * Build year/period/location/user dropdown option sets from cycle list.
 *
 * @param {Object[]} cycles
 * @param {Object[]} locationsList
 * @param {Object[]} usersList
 * @returns {{ locations: Object[], users: Object[], periods: Object[], availableMonthTokens: string[] }}
 */
export function buildFilterOptions(cycles, locationsList = [], usersList = []) {
  const yearSet = new Set();
  const monthTokenSet = new Set();
  const userIdSet = new Set();

  (cycles || []).forEach((cycle) => {
    const from = toDate(cycle.from);
    const to = toDate(cycle.to);
    if (from && to) {
      enumerateMonthTokensBetween(from, to).forEach((token) => {
        yearSet.add(token.slice(0, 4));
        monthTokenSet.add(token);
      });
    } else {
      [cycle.from, cycle.to].forEach((dateValue) => {
        const d = toDate(dateValue);
        if (!d) return;
        const year = d.getUTCFullYear();
        const month = d.getUTCMonth() + 1;
        yearSet.add(String(year));
        monthTokenSet.add(`${year}-${String(month).padStart(2, '0')}`);
      });
    }
    (cycle.managerlist || []).forEach((id) => userIdSet.add(id));
    (cycle.userlist || []).forEach((id) => userIdSet.add(id));
  });

  const years = Array.from(yearSet).sort((a, b) => Number(b) - Number(a));
  const months = Array.from(monthTokenSet).sort().reverse();

  const periods = [
    ...years.map((y) => ({ _id: y, label: `${y} All` })),
    ...months.map((token) => {
      const [y, m] = token.split('-');
      const monthIndex = Number(m) - 1;
      return {
        _id: token,
        label: `${MONTH_SHORT_NAMES[monthIndex]} ${y}`,
      };
    }),
  ];

  const locations = (locationsList || []).map((loc) => ({
    _id: loc._id,
    label: loc.name || loc.label,
    name: loc.name || loc.label,
  }));

  const usersById = {};
  (usersList || []).forEach((u) => {
    usersById[u._id] = u;
  });

  const users = Array.from(userIdSet)
    .map((id) => {
      const u = usersById[id];
      return {
        _id: id,
        label: u ? u.name || u.displayname || id : id,
        name: u ? u.name || u.displayname || id : id,
      };
    })
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));

  return { locations, users, periods, availableMonthTokens: months };
}
